import { Component, OnInit, OnDestroy } from "@angular/core";
import { Subject, forkJoin } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { DispatchService } from "../dispatch-schedule/shared/dispatch.service";
import { ToastrService } from "ngx-toastr";
import {
  DispatchCard,
  DriverCard,
  TrailerCard,
  TractorCard,
} from "@app/shared/components/dispatch-map/dispatch-map.component";
import { GetPictureService } from "../shared/services/files/getPictures.service";
import { getDriverTypeLabel as driverTypeLabel } from "@app/shared/utils/driver-type.util";

@Component({
  selector: "app-home-component",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.css"],
})
export class HomeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  // Data
  allCards: DispatchCard[] = [];
  cards: DispatchCard[] = [];
  schedulingTeams: any[] = [];
  loading = true;

  // Search
  searchText = "";

  // Map
  mapCenter: google.maps.LatLngLiteral = { lat: 39.8283, lng: -98.5795 };
  mapZoom = 4;
  showTypes: { drivers: boolean; trailers: boolean; tractors: boolean } = {
    drivers: true,
    trailers: true,
    tractors: true,
  };

  // Card pinned by clicking a map marker (shown in the corner of the map)
  selectedCard: DispatchCard | null = null;

  constructor(
    private dispatchService: DispatchService,
    private toastr: ToastrService,
    private getPictureService: GetPictureService
  ) {}

  ngOnInit(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.applySearch());

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchText);
  }

  clearSearch(): void {
    this.searchText = "";
    this.applySearch();
  }

  private loadData(): void {
    this.loading = true;

    forkJoin({
      dispatch: this.dispatchService.getDispatchData(),
      teams: this.dispatchService.getAllSchedulingTeams(),
    }).subscribe({
      next: ({ dispatch: data, teams }: { dispatch: any; teams: any[] }) => {
        this.schedulingTeams = teams;
        this.allCards = [
          ...this.mapDrivers(data.drivers),
          ...this.mapTrailers(data.trailers),
          ...this.mapTractors(data.tractors),
        ];
        this.applySearch();
        this.loading = false;
      },
      error: (error) => {
        console.error("Failed to load dispatch data:", error);
        this.toastr.error("Failed to load dispatch data");
        this.loading = false;
      },
    });
  }

  private applySearch(): void {
    const search = this.searchText.toLowerCase().trim();

    if (!search) {
      this.cards = [...this.allCards];
      this.dropPinIfFilteredOut();
      return;
    }

    this.cards = this.allCards.filter((card) => {
      const s = search;

      if (card.type === "driver") {
        const d = card as DriverCard;
        return (
          d.name?.toLowerCase().includes(s) ||
          d.driverNo?.toLowerCase().includes(s) ||
          d.tractorNumber?.toLowerCase().includes(s) ||
          d.trailerNumber?.toLowerCase().includes(s) ||
          d.roadCallNumber?.toLowerCase().includes(s) ||
          d.roadCallStatus?.toLowerCase().includes(s) ||
          d.schedulingTeamName?.toLowerCase().includes(s) ||
          d.schedulingTeamBoardName?.toLowerCase().includes(s)
        );
      }

      if (card.type === "tractor") {
        const t = card as TractorCard;
        return (
          t.tractorNumber?.toLowerCase().includes(s) ||
          t.roadCallNumber?.toLowerCase().includes(s) ||
          t.roadCallStatus?.toLowerCase().includes(s)
        );
      }

      if (card.type === "trailer") {
        const t = card as TrailerCard;
        return (
          t.trailerNumber?.toLowerCase().includes(s) ||
          t.roadCallNumber?.toLowerCase().includes(s) ||
          t.roadCallStatus?.toLowerCase().includes(s)
        );
      }

      return false;
    });

    this.dropPinIfFilteredOut();
  }

  /** Clears the pinned card when search removes that unit from the map. */
  private dropPinIfFilteredOut(): void {
    const pinned = this.selectedCard;
    if (!pinned) return;
    const stillVisible = this.cards.some((c) => c.id === pinned.id && c.type === pinned.type);
    if (!stillVisible) this.selectedCard = null;
  }

  private buildDriverTeamLookup(): Map<string, { teamName: string; boardName: string | null }> {
    const lookup = new Map<string, { teamName: string; boardName: string | null }>();
    for (const team of this.schedulingTeams) {
      for (const driver of team.drivers ?? []) {
        lookup.set(driver.driverId, { teamName: team.name, boardName: driver.boardName || null });
      }
    }
    return lookup;
  }

  private mapDrivers(drivers: any[]): DriverCard[] {
    const teamLookup = this.buildDriverTeamLookup();
    return drivers.map((d) => ({
      type: "driver",
      id: d.id,
      name: d.name,
      driverNo: d.driverNo,
      driverType: d.driverType,
      pictureUrl: d.pictureUrl,
      location: d.location,
      locationAge: this.calculateLocationAge(d.locationDateTime),
      latitude: d.latitude,
      longitude: d.longitude,
      milesLast7Days: d.milesLast7Days || 0,
      homeCity: d.homeCity,
      homeState: d.homeState,
      hoursOfService: d.rawHoursOfService?.dsaUsa708Hours || d.hoursOfService || null,
      assigned: d.isAssigned,
      selected: false,
      tractorId: d.tractorId,
      trailerId: d.trailerId,
      tractorNumber: d.tractorNumber || null,
      trailerNumber: d.trailerNumber || null,
      attachedTrailerNumbers: Array.isArray(d.attachedTrailerNumbers)
        ? d.attachedTrailerNumbers
        : [],
      schedulingTeamName: teamLookup.get(d.id)?.teamName || null,
      schedulingTeamBoardName: teamLookup.get(d.id)?.boardName || null,
      productQualifications: Array.isArray(d.productQualifications) ? d.productQualifications : [],
      prequalifiedProductQualifications: Array.isArray(d.prequalifiedProductQualifications)
        ? d.prequalifiedProductQualifications
        : [],
      roadCallStatus: d.roadCallStatus || null,
      roadCallNumber: d.roadCallNumber || null,
      roadCallOpenedDate: d.roadCallOpenedDate || null,
    }));
  }

  private mapTrailers(trailers: any[]): TrailerCard[] {
    return trailers.map((t) => ({
      type: "trailer",
      id: t.id,
      trailerNumber: t.trailerNumber,
      equipmentCode: t.equipmentCode,
      currentLocation: t.currentLocation,
      locationAge: this.calculateLocationAge(t.locationDateTime),
      latitude: t.latitude,
      longitude: t.longitude,
      terminal: t.terminal,
      inService: t.inService,
      assigned: t.isAssigned,
      nextServiceDate: t.nextServiceDate ? new Date(t.nextServiceDate) : null,
      daysUntilNextService: this.calculateDaysUntilService(t.nextServiceDate),
      selected: false,
      attachedTractorNumber: t.attachedTractorNumber || null,
      roadCallStatus: t.roadCallStatus || null,
      roadCallNumber: t.roadCallNumber || null,
      roadCallOpenedDate: t.roadCallOpenedDate || null,
    }));
  }

  private mapTractors(tractors: any[]): TractorCard[] {
    return tractors.map((t) => ({
      type: "tractor",
      id: t.id,
      tractorNumber: t.tractorNumber,
      currentLocation: t.currentLocation || t.parsedLocation,
      locationAge: this.calculateLocationAge(t.locationDateTime),
      latitude: t.latitude,
      longitude: t.longitude,
      terminal: t.terminal,
      inService: t.inService,
      assigned: t.isAssigned,
      nextServiceDate: t.nextServiceDate ? new Date(t.nextServiceDate) : null,
      daysUntilNextService: this.calculateDaysUntilService(t.nextServiceDate),
      selected: false,
      attachedTrailerNumbers: Array.isArray(t.attachedTrailerNumbers)
        ? t.attachedTrailerNumbers
        : [],
      roadCallStatus: t.roadCallStatus || null,
      roadCallNumber: t.roadCallNumber || null,
      roadCallOpenedDate: t.roadCallOpenedDate || null,
    }));
  }

  // ── Pinned marker card ──

  onMapMarkerClick(card: DispatchCard): void {
    // Click the same marker again to unpin it
    const pinned = this.selectedCard;
    this.selectedCard = pinned && pinned.id === card.id && pinned.type === card.type ? null : card;
  }

  closeSelectedCard(): void {
    this.selectedCard = null;
  }

  get selectedCardType(): "driver" | "tractor" | "trailer" | null {
    return this.selectedCard?.type ?? null;
  }

  get selectedTitle(): string {
    const c = this.selectedCard;
    if (!c) return "";
    if (c.type === "tractor") return `Tractor ${c.tractorNumber}`;
    if (c.type === "trailer") return `Trailer ${c.trailerNumber}`;
    return "Driver";
  }

  get selectedDriver(): DriverCard | null {
    const c = this.selectedCard;
    return c && c.type === "driver" ? c : null;
  }

  get selectedTractor(): TractorCard | null {
    const c = this.selectedCard;
    return c && c.type === "tractor" ? c : null;
  }

  get selectedTrailer(): TrailerCard | null {
    const c = this.selectedCard;
    return c && c.type === "trailer" ? c : null;
  }

  get selectedRoadCall(): {
    status: string;
    number: string | null;
    opened: Date | string | null;
  } | null {
    const c = this.selectedCard;
    if (!c || !c.roadCallStatus) return null;
    return {
      status: c.roadCallStatus,
      number: c.roadCallNumber ?? null,
      opened: c.roadCallOpenedDate ?? null,
    };
  }

  getDriverPictureUrl(card: DriverCard): string {
    if (card.pictureUrl) {
      return this.getPictureService.getPicturePath(card.pictureUrl, "driver");
    }
    return "assets/img/tractordriver.jpg";
  }

  getDriverTypeLabel(type: string): string {
    return driverTypeLabel(type);
  }

  /** Mirrors the road call colours used on the equipment report / tractor detail. */
  getRoadCallStatusClass(status?: string | null): string {
    const s = (status || "").toUpperCase();
    if (s.includes("COMPLETE") || s.includes("CLOSED")) return "road-call-complete";
    if (s.includes("HOLD") || s.includes("WAIT")) return "road-call-waiting";
    if (s.includes("ESTIMAT") || s.includes("OPEN")) return "road-call-estimating";
    return "road-call-active";
  }

  private calculateLocationAge(dateTime: Date | null): string | null {
    if (!dateTime) return null;
    const now = new Date();
    const locationDate = new Date(dateTime);
    const diffMs = now.getTime() - locationDate.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 1) return "< 1 hour ago";
    if (diffHours === 1) return "1 hour ago";
    if (diffHours < 24) return `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "1 day ago";
    return `${diffDays} days ago`;
  }

  private calculateDaysUntilService(serviceDate: Date | null): number | null {
    if (!serviceDate) return null;
    const now = new Date();
    const service = new Date(serviceDate);
    const diffMs = service.getTime() - now.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }
}
